import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { BsLayers, BsClock, BsExclamationTriangle, BsCheckCircle } from 'react-icons/bs';
import { INK, STATUS } from 'Utils/styleTokens';
import { explainJobError } from 'Utils/errorText';
import { ErrorDetailsDialog } from 'Utils/ErrorDetails';

/** Human-readable masking duration: sub-minute as seconds, otherwise "Nm Ms". */
const formatDuration = (ms) => {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
};

const StatRow = ({ icon, label, value, tone, title, onClick, testId }) => (
  <div
    className='d-flex align-items-center justify-content-between py-2'
    style={{ borderBottom: `1px solid ${INK[100]}` }}
    title={title || undefined}
  >
    <span className='d-flex align-items-center' style={{ color: INK[500], fontSize: '13px' }}>
      <span className='mr-2 d-flex'>{icon}</span>
      {label}
    </span>
    {onClick ? (
      <button
        type='button'
        className='btn btn-link p-0'
        style={{ fontWeight: 600, fontSize: '16px', color: tone || INK[900], textDecoration: 'underline' }}
        onClick={onClick}
        title='Show what failed and what to do next'
        data-testid={testId}
      >
        {value}
      </button>
    ) : (
      <span style={{ fontWeight: 600, fontSize: '16px', color: tone || INK[900] }}>{value}</span>
    )}
  </div>
);

/**
 * Left panel of the Masking/Encryption Summary — headline stats for the latest job. Labels default
 * to masking wording; the encryption summary overrides `heading` and `rowsLabel`.
 */
const MaskingStatus = ({
  rowsMasked = null,
  durationMs = null,
  errorCount = null,
  errorDetail = null,
  heading = 'MASKING STATS',
  rowsLabel = 'Rows masked',
}) => {
  const duration = formatDuration(durationMs);
  const hasErrors = Number(errorCount) > 0;
  const [showErrors, setShowErrors] = useState(false);
  const explained = hasErrors && errorDetail ? explainJobError(errorDetail) : null;

  return (
    <div className='h-100 d-flex flex-column'>
      <div>
        <h5 className='text-left h-secondary'>{heading}</h5>
      </div>
      <Card className='flex-grow-1 w-100' style={{ maxWidth: '320px' }}>
        <Card.Body className='d-flex flex-column justify-content-center text-left'>
          <StatRow
            icon={<BsLayers size={15} color={INK[400]} />}
            label={rowsLabel}
            value={rowsMasked != null ? Number(rowsMasked).toLocaleString() : '—'}
          />
          <StatRow
            icon={<BsClock size={15} color={INK[400]} />}
            label='Duration'
            value={duration || '—'}
          />
          <StatRow
            icon={hasErrors
              ? <BsExclamationTriangle size={15} color={STATUS.danger.text} />
              : <BsCheckCircle size={15} color={STATUS.success.text} />}
            label='Errors'
            value={errorCount == null ? '—' : (hasErrors ? Number(errorCount).toLocaleString() : 'None')}
            tone={hasErrors ? STATUS.danger.text : (errorCount === 0 ? STATUS.success.text : undefined)}
            title={explained ? explained.headline : undefined}
            onClick={explained ? () => setShowErrors(true) : undefined}
            testId='masking-status-errors'
          />
          {explained && (
            <ErrorDetailsDialog show={showErrors} onHide={() => setShowErrors(false)} title='Masking errors' {...explained} />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default MaskingStatus;
